import { __rest } from "tslib";
import styled from '@emotion/styled';
import React, { forwardRef } from 'react';
import { theme } from '../../../theme';
import { useField } from 'formik';
import { getErrorIconStyles, getErrorStyles } from './styles';
export const StyledInputRoot = styled.div `
	display: flex;
	flex-direction: row;

	${theme.typography.IR100};
	line-height: 150%;

	width: 100%;
	border: solid 1px;
	border-radius: ${theme.radii.radii30};
	border-color: ${theme.colors.LM_NU30};

	color: #11233a;
	background-color: ${theme.colors.LM_NU10};
	outline-offset: -2px;

	:hover {
		background-color: ${theme.colors.LM_NU20};
	}

	:active {
		outline: 2px solid ${theme.colors.BB100};
		background-color: ${theme.colors.LM_NU20};
	}
	:focus-within {
		outline: 2px solid ${theme.colors.BB80};
		background-color: ${theme.colors.LM_NU20};
	}

	${({ error, touched }) => (Object.assign({}, getErrorStyles(error, touched)))};
`;
const StyledLabel = styled.label `
	display: flex;
	flex-direction: column;
	margin-bottom: 1rem;
	width: 100%;
	padding: 0.25rem 0;
	position: relative;
`;
export const StyledTitle = styled.span `
	${theme.typography.IR90};
	color: ${theme.colors.LM_NU100};
	height: 1.125rem;
	line-height: 125%;
	margin-bottom: 0.25rem;
	user-select: none;
`;
export const StyledDescription = styled.span `
	${theme.typography.IR90};
	color: rgba(17, 34, 58, 0.65);
	font-size: 0.875rem;
	line-height: 150%;
	margin-bottom: 0.5rem;
`;
export const StyledInput = styled.input `
	all: unset;
	flex: inherit;
	padding: 0.625rem 0.75rem 0.625rem 1rem;
	position: relative;
	color: inherit;
	background-color: inherit;
`;
const StyledErrorMsg = styled.span `
	${theme.typography.IR90};
	color: ${theme.colors.NE100};
	padding: 0.5rem 0;
`;
const StyledErrorIcon = styled.span `
	position: relative;
	display: flex;
	flex-grow: 1;
	flex-shrink: 1;
	min-width: 0;

	${({ error, touched }) => ({
    ':after': Object.assign({}, getErrorIconStyles(error, touched)),
})};
`;
const Prefix = styled.span `
	${theme.typography.IR100};
	line-height: 150%;

	color: ${theme.colors.LM_NU100};
	user-select: none;
	flex-grow: 0;
	flex-shrink: 0;

	width: auto;
	padding: 0.625rem 1rem 0.625rem 1rem;
	height: 100%;

	border-right: 1px solid ${theme.colors.LM_NU30};
	border-radius: ${theme.radii.radii30} 0 0 ${theme.radii.radii30} !important;
	/* !important on background-color to override the hover/active/focus of the parent */
	background-color: rgba(10, 24, 43, 0.03) !important;
`;
const Suffix = styled(Prefix) `
	border-left: 1px solid ${theme.colors.LM_NU30};
	border-right: none;
	border-radius: 0 ${theme.radii.radii30} ${theme.radii.radii30} 0 !important;
`;
// Internal InputField component
export const InternalTextInput = forwardRef((props, ref) => {
    const { label, fieldPrefix, fieldSuffix, runValidationWithoutTouch } = props;
    // Formik connector
    const [field, meta] = useField(props);
    const { touched, error } = meta;
    // should show validation error without the touch requirement if the  runValidationWithoutTouch prop is true
    const shouldShowValidationError = runValidationWithoutTouch ? !!error : touched && !!error;
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledInputRoot, { touched: touched, error: error },
            fieldPrefix && React.createElement(Prefix, null, fieldPrefix),
            React.createElement(StyledErrorIcon, { touched: touched, error: error },
                React.createElement(StyledInput, Object.assign({ ref: ref, "data-testid": "input", "aria-label": props['aria-label'] || label }, field, meta, props))),
            fieldSuffix && React.createElement(Suffix, null, fieldSuffix)),
        shouldShowValidationError && React.createElement(StyledErrorMsg, null, error)));
});
// Shared component with added styling for title and description
export const TextInputWrapper = ({ label, description, style, className, children, }) => {
    return (React.createElement(StyledLabel, { className: className, style: style },
        React.createElement(StyledTitle, null, label),
        description && (React.createElement(StyledDescription, null, description)),
        children));
};
export const TextInput = (_a) => {
    var { label, description, style, className } = _a, otherProps = __rest(_a, ["label", "description", "style", "className"]);
    return (React.createElement(TextInputWrapper, Object.assign({ label: label, description: description, style: style, className: className }, otherProps),
        React.createElement(InternalTextInput, Object.assign({ label: label }, otherProps))));
};
//# sourceMappingURL=TextInput.js.map